import { getUsers, getAllUsers, getPickerUsers, createUser, updateUser, deleteUser } from '../models/userModel.js';
import { sendWelcomeMail } from '../lib/welcomeMailApi.js';
import Swal from 'sweetalert2';
import pb from '../lib/pocketbase';
import { toasts } from "svelte-toasts";

const READONLY_USER_FIELDS = new Set([
  'id',
  'collectionId',
  'collectionName',
  'created',
  'updated',
  'expand',
  'verified',
  'role_descrip',
  'sucursal_descrip',
  'empresa_descrip',
  'vendedor_nombre',
  'egresoDet',
  'escoltas',
  'corredores',
  'pickers',
]);

function formatPbUserError(error, fallback = 'Ocurrió un error al guardar el usuario') {
  const data = error?.response || error?.data;
  const fieldErrors = data?.data;

  if (fieldErrors && typeof fieldErrors === 'object') {
    const parts = Object.entries(fieldErrors).map(([field, detail]) => {
      if (typeof detail === 'string') return `${field}: ${detail}`;
      if (detail?.message) return `${field}: ${detail.message}`;
      return `${field}: inválido`;
    });
    if (parts.length) return parts.join(', ');
  }

  return data?.message || error?.message || fallback;
}

function buildCreateUserPayload(user) {
  const password = user.password?.trim();
  const passwordConfirm = user.passwordConfirm?.trim() || password;

  if (!user.email?.trim()) {
    throw new Error('Email requerido');
  }
  if (!password) {
    throw new Error('Contraseña requerida');
  }
  if (password.length < 8) {
    throw new Error('La contraseña debe tener al menos 8 caracteres');
  }
  if (password !== passwordConfirm) {
    throw new Error('Las contraseñas no coinciden');
  }

  const payload = {
    email: user.email.trim(),
    password,
    passwordConfirm,
    name: user.name?.trim() || '',
    emailVisibility: user.emailVisibility !== false,
  };

  if (user.role) {
    payload.role = user.role;
  }

  if (user.avatar instanceof File) {
    payload.avatar = user.avatar;
  }

  return payload;
}

function buildUpdateUserPayload(user) {
  if (!user.email?.trim()) {
    throw new Error('Email requerido');
  }

  const payload = {
    email: user.email.trim(),
    name: user.name?.trim() || '',
    emailVisibility: user.emailVisibility !== false,
  };

  if (user.role) {
    payload.role = user.role;
  }

  if (user.avatar instanceof File) {
    payload.avatar = user.avatar;
  }

  return payload;
}

function stripUserFields(user) {
  const clean = {};
  Object.entries(user || {}).forEach(([key, value]) => {
    if (!READONLY_USER_FIELDS.has(key)) {
      clean[key] = value;
    }
  });
  return clean;
}

// Obtener lista de usuarios paginados
export async function fetchUsers(page = 1, perPage = 200) {
  try {
    const response = await getUsers(page, perPage);
    return {
      users: response.items.map((user) => ({
        ...user,
        email: user.email || user.username || '',
        role_descrip: user.expand?.role?.descrip || '',
      })),
      totalRecords: response.totalItems,
      currentPage: response.page,
    };
  } catch (error) {
    console.error("Error fetching users:", error);
    Swal.fire('Error', 'No se pudieron cargar los usuarios', 'error');
    return null;
  }
}

export async function fetchAllUsers() {
  try {
    const items = await getAllUsers();
    return { users: items, totalRecords: items.length, currentPage: 1 };
  } catch (error) {
    console.error('Error fetching all users:', error);
    return null;
  }
}

export async function fetchPickerUsers() {
  try {
    const items = await getPickerUsers();
    return { users: items, totalRecords: items.length, currentPage: 1 };
  } catch (error) {
    console.error('Error fetching picker users:', error);
    return null;
  }
}

// Guardar o actualizar un usuario
export async function saveUser(user, currentUserId) {
  const { verified } = user;
  const plainUser = stripUserFields(user);
  const plainPassword = plainUser.password;

  try {
    if (currentUserId) {
      await updateUser(currentUserId, buildUpdateUserPayload(plainUser));

      if (verified) {
        await verifyUser(currentUserId);
      }

      toasts.add({
        title: 'Éxito',
        description: 'Usuario actualizado exitosamente',
        duration: 3000,
        placement: 'bottom-right',
        type: 'success',
        theme: 'dark',
        showProgress: true,
      });
      return;
    }

    const created = await createUser(buildCreateUserPayload(plainUser));

    if (verified) {
      await verifyUser(created.id);
    }

    try {
      await sendWelcomeMail({
        email: created.email,
        name: created.name,
        password: plainPassword,
      });
    } catch (mailError) {
      console.warn('Welcome mail failed:', mailError);
      toasts.add({
        title: 'Usuario creado',
        description: 'La cuenta se creó, pero no se pudo enviar el correo de bienvenida',
        duration: 4000,
        placement: 'bottom-right',
        type: 'warning',
        theme: 'dark',
        showProgress: true,
      });
      return;
    }

    toasts.add({
      title: 'Éxito',
      description: 'Usuario creado y correo de bienvenida enviado',
      duration: 3000,
      placement: 'bottom-right',
      type: 'success',
      theme: 'dark',
      showProgress: true,
    });
  } catch (error) {
    console.error("Error saving user:", error);
    toasts.add({
      title: 'Error',
      description: formatPbUserError(error),
      duration: 4000,
      placement: 'bottom-right',
      type: 'error',
      theme: 'dark',
      showProgress: true,
    });
    throw error;
  }
}

// Eliminar un usuario
export async function removeUser(id) {
  try {
    await deleteUser(id);
    toasts.add({
      title: 'Éxito',
      description: 'Usuario eliminado exitosamente.',
      duration: 3000,
      placement: 'bottom-right',
      type: 'error',
      theme: 'dark',
      showProgress: true,
    });
  } catch (error) {
    console.error("Error deleting user:", error);
    Swal.fire('Error', 'No se pudo eliminar el usuario', 'error');
  }
}

// Suscripción en tiempo real a cambios en la colección 'users'
export function subscribeToUsers(searchQuery, handleSearch) {
  pb.collection('users').subscribe('*', async () => {
    if (typeof handleSearch === 'function') {
      await handleSearch(1);
    }
  });
}

// Desuscripción de los eventos en tiempo real
export function unsubscribeFromUsers() {
  pb.collection('users').unsubscribe('*');
}

// Buscar usuarios por nombre o email con paginación
export async function searchUsers(query, page = 1, perPage = 10) {
  const response = await pb.collection('users').getList(page, perPage, {
    filter: `username ~ "${query}" || email ~ "${query}"`,
    expand: 'role',
  });
  return {
    users: response.items.map((user) => ({
      ...user,
      email: user.email || user.username || '',
      role_descrip: user.expand?.role?.descrip || '',
    })),
    totalRecords: response.totalItems,
    currentPage: response.currentPage,
  };
}

// Cambiar contraseña de usuario
export async function changePassword(userId, newPassword, oldPassword) {
  try {
    await pb.collection('users').update(userId, {
      oldPassword: oldPassword,
      password: newPassword,
      passwordConfirm: newPassword
    });
    
    toasts.add({
      title: 'Éxito',
      description: 'Contraseña actualizada exitosamente',
      duration: 3000,
      placement: 'bottom-right',
      type: 'success',
      theme: 'dark',
      showProgress: true,
    });
    return true;
  } catch (error) {
    console.error("Error changing password:", error);
    toasts.add({
      title: 'Error',
      description: 'No se pudo cambiar la contraseña',
      duration: 3000,
      placement: 'bottom-right',
      type: 'error',
      theme: 'dark',
      showProgress: true,
    });
    return false;
  }
}

export async function resendWelcomeUser(user) {
  const userId = typeof user === 'string' ? user : user?.id;
  if (!userId) {
    throw new Error('Usuario no válido');
  }

  const result = await Swal.fire({
    title: '¿Reenviar bienvenida?',
    text: 'Se enviará de nuevo el correo de bienvenida al usuario.',
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#3085d6',
    cancelButtonColor: '#6b7280',
    confirmButtonText: 'Sí, reenviar',
    cancelButtonText: 'Cancelar',
  });

  if (!result.isConfirmed) {
    return false;
  }

  try {
    const record = await pb.collection('users').getOne(userId, { expand: 'role' });
    const email = (record.email || record.username || '').trim();

    if (!email) {
      throw new Error('No se encontró correo para este usuario');
    }

    const mailResult = await sendWelcomeMail({
      email,
      name: record.name,
      resend: true,
      userId: record.id,
    });

    const description = mailResult?.fallback === 'password_reset'
      ? `Se envió enlace de acceso a ${email} (el servidor aún no tiene el hook actualizado)`
      : `Correo de bienvenida reenviado a ${email}`;

    toasts.add({
      title: 'Éxito',
      description,
      duration: 3000,
      placement: 'bottom-right',
      type: 'success',
      theme: 'dark',
      showProgress: true,
    });
    return true;
  } catch (error) {
    console.error('Error resending welcome mail:', error);
    toasts.add({
      title: 'Error',
      description: error.message || 'No se pudo reenviar el correo de bienvenida',
      duration: 4000,
      placement: 'bottom-right',
      type: 'error',
      theme: 'dark',
      showProgress: true,
    });
    return false;
  }
}

// Verificar un usuario
export async function verifyUser(userId) {
  try {
    const user = await pb.collection('users').getOne(userId);
    await pb.collection('users').requestVerification(user.email);
    
    toasts.add({
      title: 'Éxito',
      description: 'Se ha enviado un correo de verificación al usuario',
      duration: 3000,
      placement: 'bottom-right',
      type: 'success',
      theme: 'dark',
      showProgress: true,
    });
    return true;
  } catch (error) {
    console.error("Error verifying user:", error);
    toasts.add({
      title: 'Error',
      description: 'No se pudo enviar el correo de verificación',
      duration: 3000,
      placement: 'bottom-right',
      type: 'error',
      theme: 'dark',
      showProgress: true,
    });
    return false;
  }
}
